import { EditorState } from 'draft-js';

import { Collapse } from '@components/animators/collapse';
import { Button } from '@components/button';
import { Card } from '@components/card';
import { FormError } from '@components/form-error';
import { FormLabel } from '@components/form-label';
import { FormInstance } from '@components/form/create-form';
import { FormStoreInstance } from '@components/form/create-form-store';
import { IconOpenPreview } from '@components/icons/open-preview';
import { LabelTextGroup } from '@components/label-text-group';
import { Stack } from '@components/stack';
import { TextEditor } from '@components/text-editor';
import { useScopedTranslate } from '@locale/translate';
import { asteriskLabel } from '@utils/strings';

import { EmailTemplateData } from 'api/jobs/requests';
import { TextEditorToolbar } from 'components/TextEditorToolbar';
import { usePlaceholderMap } from 'hooks/use-placeholder-map';

import {
  convertFromEditorStateToHtml,
  SettingsSwitch,
  SettingsTitle,
  SettingsInfoBox,
} from './common';
import { usePreviewEmailModal } from './email-preview-modal';

import { EmailFormValues, SettingsLocale } from '../settings';

type EmailSettingsProps = {
  form: FormInstance<EmailFormValues>;
  initialData: EmailTemplateData;
  store: FormStoreInstance;
  emailEditorState: EditorState;
  isEmailEmpty: boolean;
  setEmailEditorState: (editorState: EditorState) => void;
};

export const EmailSettings = ({
  form: Form,
  store,
  emailEditorState,
  isEmailEmpty,
  setEmailEditorState,
  initialData,
}: EmailSettingsProps) => {
  const { t } = useScopedTranslate(SettingsLocale);
  const showPreviewEmailModal = usePreviewEmailModal();
  const placeholderMap = usePlaceholderMap('applicant_new_application');
  const formValues = store.useFormValues();
  const formUseCompanyEmail = formValues.useCompanyEmail as boolean;
  const showEmailError = !formUseCompanyEmail && isEmailEmpty;

  const initialUseCompany = initialData.useCompanyEmailTemplate ?? true;
  const isUseCompanyEmail =
    (formValues.useCompanyEmail as boolean | undefined) ?? initialUseCompany;

  return (
    <Card variant="transparent" space={6}>
      <Stack space={4}>
        <SettingsTitle title={t('applicationEmailResponse')} />
        <SettingsInfoBox text={t('emailInfoText')} />
        <SettingsSwitch<EmailFormValues>
          label={t('useCompanyDefault')}
          enabledText={t('yes')}
          disabledText={t('no')}
          fieldId="useCompanyEmail"
          form={Form}
          isEnabled={isUseCompanyEmail}
          defaultValue={initialUseCompany}
          dataCy="email-switch"
        />
        {isUseCompanyEmail && (
          <LabelTextGroup
            label={t('emailTemplateTitle')}
            text={initialData.companyTemplate?.title ?? ''}
          />
        )}

        {!isUseCompanyEmail && <SettingsInfoBox text={t('emailSubjectInfoText')} />}
        <Collapse isCollapsed={isUseCompanyEmail}>
          <Stack space={4}>
            <Stack space={2}>
              <Stack space={1}>
                <FormLabel text={asteriskLabel(t('content'), true)} />
                <TextEditor
                  variant="gray"
                  initialEditorState={emailEditorState}
                  onChange={setEmailEditorState}
                  error={showEmailError}
                >
                  <Stack orientation="horizontal" justify="space-between" align="center">
                    <TextEditorToolbar />

                    <TextEditor.PlaceholderMenu
                      label={t('placeholder')}
                      placeholderMap={placeholderMap}
                    />
                  </Stack>
                  <TextEditor.Input />
                </TextEditor>
                {showEmailError && <FormError text={t('requiredField')} />}
              </Stack>
            </Stack>
          </Stack>
        </Collapse>
        <Stack orientation="horizontal" justify="flex-end">
          <Button
            text={t('previewEmail')}
            variant="secondary"
            iconLeft={<IconOpenPreview />}
            onClick={() =>
              showPreviewEmailModal({
                subject: initialData.subject as string,
                content:
                  (isUseCompanyEmail
                    ? initialData.companyTemplate?.contentHtml
                    : convertFromEditorStateToHtml(emailEditorState)) ?? '',
              })
            }
          />
        </Stack>
      </Stack>
    </Card>
  );
};
